import React from 'react';
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import { useAuth0 } from '@auth0/auth0-react';
import { useEntity } from './EntityContext';
import { flagOverridePlugin, eventInterceptionPlugin } from './launchDarklyPlugins';
import type { LDContext } from 'launchdarkly-js-client-sdk';

interface LaunchDarklyProviderProps {
  children: React.ReactNode;
}

const clientSideId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID;

const PassthroughProvider = ({ children }: LaunchDarklyProviderProps) => {
  return <>{children}</>;
};

// LDProvider.componentDidUpdate only re-identifies when deferInitialization: true,
// so we must call identify() manually when auth/entity context changes.
//
// The ref tracks the last context we've identified with, seeded from
// ldClient.getContext() the first time ldClient becomes available — this lets us
// skip the evalx LDProvider already performed on init() without re-issuing it.
// If the context drifted while ldClient was initializing (e.g., auth resolved
// before waitForInitialization() completed), the keys will differ and we fall
// through to call identify() immediately.
const ContextUpdater = ({ context }: { context: LDContext }) => {
  const ldClient = useLDClient();
  const lastContextRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!ldClient || typeof ldClient.identify !== 'function') return;

    const contextKey = JSON.stringify(context);

    if (lastContextRef.current === null) {
      const initCtx = ldClient.getContext?.();
      const initKey = initCtx ? JSON.stringify(initCtx) : contextKey;
      lastContextRef.current = initKey;
      if (initKey === contextKey) return;
    }

    if (lastContextRef.current === contextKey) return;
    lastContextRef.current = contextKey;
    ldClient.identify(context);
  }, [ldClient, context]);

  return null;
};

export const LaunchDarklyProvider = ({ children }: LaunchDarklyProviderProps) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const { activeEntity } = useEntity();

  // Extract values to make dependency array cleaner
  const userEmail = user?.email;
  const userName = user?.name;
  const entityId = activeEntity?.id;
  const entityFriendlyId = activeEntity?.friendlyId;
  const entityDisplayName = activeEntity?.displayName;

  const ldContext = React.useMemo(
    () => ({
      kind: 'multi' as const,
      user: {
        key: entityFriendlyId ?? 'anonymous',
        ...(!isLoading && isAuthenticated && userEmail && { email: userEmail }),
        ...(!isLoading && isAuthenticated && userName && { name: userName }),
      },
      entity: entityFriendlyId
        ? {
            key: entityFriendlyId,
            id: entityId,
            name: entityDisplayName,
          }
        : {
            key: 'anonymous',
          },
    }),
    [
      userEmail,
      userName,
      isAuthenticated,
      isLoading,
      entityId,
      entityFriendlyId,
      entityDisplayName,
    ]
  );

  if (!clientSideId) {
    return <PassthroughProvider>{children}</PassthroughProvider>;
  }

  return (
    <LDProvider
      clientSideID={clientSideId}
      context={ldContext}
      timeout={5}
      options={{
        bootstrap: 'localStorage',
        plugins: [flagOverridePlugin, eventInterceptionPlugin],
      }}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      <ContextUpdater context={ldContext} />
      {children}
    </LDProvider>
  );
};
